"use client"

import { useEffect, useState } from "react"
import type { BooruPost } from "@/lib/booru-api"

// Moved out of components/booru-detail-modal.tsx (same state, same effects, same dependencies). These effects only fetch
// and store a URL, so running them from here (a little earlier in the modal's effect order) changes nothing.
export function useLazyMediaUrls({ open, post, isVideo }: { open: boolean; post: BooruPost | null; isVideo: boolean }) {
  // Lazy loading for Rule34Hentai full images
  const [lazyLoadedImageUrl, setLazyLoadedImageUrl] = useState<string | null>(null)
  const [lazyLoadedVideoUrl, setLazyLoadedVideoUrl] = useState<string | null>(null) // For XlecX videos
  const [lazyLoadedEmbedUrl, setLazyLoadedEmbedUrl] = useState<string | null>(null) // For JAVAPI
  const [isLoadingFullImage, setIsLoadingFullImage] = useState(false)
  const [rule34VideoUrl, setRule34VideoUrl] = useState<string | null>(null)

  // Fetch actual video URL from Rule34 post page (they block hotlinking)
  useEffect(() => {
    if (post?.site === 'rule34' && isVideo && post?.id) {
      setRule34VideoUrl(null)
      fetch(`/api/booru/rule34-video?id=${post.id}`)
        .then(res => res.json())
        .then(data => {
          if (data.videoUrl) {
            console.log('[v0] Rule34 video URL fetched:', data.videoUrl)
            setRule34VideoUrl(data.videoUrl)
          } else {
            console.error('[v0] Rule34 video fetch failed:', data.error)
          }
        })
        .catch(err => {
          console.error('[v0] Rule34 video fetch error:', err)
        })
    }
  }, [post?.site, post?.id, isVideo])

  // Lazy load full image URL for Rule34Hentai
  useEffect(() => {
    if (!open || !post) {
      setLazyLoadedImageUrl(null)
      setIsLoadingFullImage(false)
      return
    }

    // Only for Rule34Hentai posts with postUrl and thumbnail fileUrl
    if (post.site === 'rule34hentai' && post.postUrl && post.fileUrl?.includes('/_thumbs/')) {
      setIsLoadingFullImage(true)
      
      // Import the function dynamically to avoid circular dependencies
      import('@/lib/booru-api').then(({ fetchRule34HentaiFullImageUrl }) => {
        fetchRule34HentaiFullImageUrl(post.postUrl!).then((fullUrl) => {
          if (fullUrl) {
            console.log(`[v0] Lazy loaded full image URL: ${fullUrl}`)
            setLazyLoadedImageUrl(fullUrl)
          } else {
            console.warn(`[v0] Could not lazy load full image URL, using thumbnail`)
          }
          setIsLoadingFullImage(false)
        }).catch((error) => {
          console.error(`[v0] Error lazy loading full image:`, error)
          setIsLoadingFullImage(false)
        })
      })
    } else {
      setLazyLoadedImageUrl(null)
      setIsLoadingFullImage(false)
    }
  }, [open, post?.id, post?.site, post?.postUrl, post?.fileUrl])

  // Lazy load video URL for XlecX videos
  useEffect(() => {
    console.log(`[v0] XlecX lazy load check:`, {
      open,
      postSite: post?.site,
      postType: post?.type,
      postUrl: post?.postUrl,
      postId: post?.id
    })
    
    if (!open || !post) {
      setLazyLoadedVideoUrl(null)
      return
    }

    // Only for XlecX video posts
    if (post.site === 'xlecx' && post.type === 'video' && post.postUrl) {
      console.log(`[v0] ✅ XlecX video detected! Lazy loading video URL from: ${post.postUrl}`)
      setIsLoadingFullImage(true)
      
      // Fetch the video URL from the post page
      fetch(`/api/booru/xlecx/gallery?url=${encodeURIComponent(post.postUrl)}`)
        .then(res => res.json())
        .then(data => {
          console.log(`[v0] XlecX gallery API response:`, data)
          console.log(`[v0] Pages in response:`, data.pages)
          
          // Find the video page
          const videoPage = data.pages?.find((p: any) => p.isVideo)
          
          console.log(`[v0] Video page found:`, videoPage)
          
          if (videoPage && videoPage.imageUrl) {
            console.log(`[v0] ✅ Lazy loaded XlecX video URL: ${videoPage.imageUrl}`)
            setLazyLoadedVideoUrl(videoPage.imageUrl)
          } else if (data.pages && data.pages.length > 0 && data.pages[0].imageUrl) {
            // Fallback: if there's only one page and it has a video URL, use it
            console.log(`[v0] ⚠️ No isVideo flag, using first page as video: ${data.pages[0].imageUrl}`)
            setLazyLoadedVideoUrl(data.pages[0].imageUrl)
          } else {
            console.warn(`[v0] ❌ Could not find video URL in XlecX gallery response`)
          }
          setIsLoadingFullImage(false)
        })
        .catch((error) => {
          console.error(`[v0] ❌ Error lazy loading XlecX video:`, error)
          setIsLoadingFullImage(false)
        })
    } else {
      console.log(`[v0] ⚠️ Not an XlecX video, skipping lazy load`)
      setLazyLoadedVideoUrl(null)
    }
  }, [open, post?.id, post?.site, post?.type, post?.postUrl])

  // Lazy fetch proper embed URL for JAVAPI posts (player API returns permanent non-blocked URL)
  useEffect(() => {
    if (!open || !post) {
      setLazyLoadedEmbedUrl(null)
      return
    }
    if ((post.site === 'javapi' || post.site === 'javapi_hentai') && post.rawPost?.postId && post.rawPost?.server) {
      const server = post.rawPost.server as string
      const postId = post.rawPost.postId as string | number
      // Use server-side proxy to avoid CORS
      fetch(`/api/booru/javapi/player?server=${encodeURIComponent(server)}&id=${postId}`)
        .then(r => r.json())
        .then(data => {
          if (data.embed_url) {
            setLazyLoadedEmbedUrl(data.embed_url)
          }
        })
        .catch(err => console.warn('[JAVAPI] Failed to fetch player embed URL:', err))
    } else {
      setLazyLoadedEmbedUrl(null)
    }
  }, [open, post?.id, post?.site, post?.rawPost?.postId, post?.rawPost?.server])

  return { lazyLoadedImageUrl, lazyLoadedVideoUrl, lazyLoadedEmbedUrl, isLoadingFullImage, rule34VideoUrl }
}
